/**
 * L.12 — `/learn/lora` landing page.
 *
 * Marketing-tier surface for the "What's a specialization?" explainer.
 * Imports the canonical `src/topics/lora.md` body via raw-loader so we
 * stay single-source-of-truth with the NIPE topic tile + the (?) popover
 * in TrainLoraButton.
 *
 * No portal scope required — this route is meant to be linkable from
 * sales pages / docs. The page renders unauthenticated.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, type ReactElement } from 'react';
import { testIds } from '../../lib/testIds';
import loraMarkdownRaw from '../../topics/lora.md?raw';
import { splitFrontmatter } from '../../lib/vault/parse';

export const Route = createFileRoute('/learn/lora')({
  component: LearnLoraPage,
});

interface FaqEntry {
  q: string;
  a: string;
}

const FAQ: ReadonlyArray<FaqEntry> = [
  {
    q: 'Does a specialization teach the model new law?',
    a: 'No. The base model already knows the law. A specialization shifts writing habits — citation style, structure, formality — based on the Skills you train on. Think house style, not new knowledge.',
  },
  {
    q: 'How is this different from just stacking Skills in a prompt?',
    a: 'Skills are prompts you compose into a single session. A specialization is a thin overlay on the base model that applies across every session it is attached to. Skills are for one-off jobs; specializations are for consistent firm-wide style.',
  },
  {
    q: 'Can the specialization see my client data?',
    a: 'No. We train only on the Skills you select, which are author-controlled, client-agnostic templates. Client-specific facts flow at runtime through the Client Genome, not through the specialization.',
  },
  {
    q: 'Can I remove it later?',
    a: 'Yes — at any time. A specialization is a few megabytes that we layer on top of the base model when you ask for it. Detach it and you have the unmodified base model back.',
  },
  {
    q: 'Why does it sometimes look subtle?',
    a: 'The demo trains a small rank-8 adapter on a single Skill against Llama-3.2-1B — fast and cheap, but light-touch. Production specializations use Qwen-8B+ and a larger Skills corpus, where the shift is more pronounced.',
  },
];

function LearnLoraPage(): ReactElement {
  const navigate = useNavigate();
  const body = useMemo(() => {
    const { body: stripped } = splitFrontmatter(loraMarkdownRaw);
    return stripped.trimStart();
  }, []);

  return (
    <div
      data-testid={testIds.learnLora.root}
      style={{
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        maxWidth: 760,
        margin: '0 auto',
        padding: '48px 24px',
        color: '#000',
        lineHeight: 1.6,
      }}
    >
      <header style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
          Repancy · primer
        </div>
        <h1
          style={{ fontSize: 36, lineHeight: 1.1, margin: '0 0 12px' }}
          data-testid={testIds.learnLora.title}
        >
          What's a specialization?
        </h1>
        <p style={{ fontSize: 18, color: '#333', margin: 0 }}>
          A thin overlay on the base model that focuses it on certain
          patterns and quiets others — built from the Skills you pick.
          Additive <em>and</em> subtractive.
        </p>
      </header>

      <article
        data-testid={testIds.learnLora.body}
        style={{ whiteSpace: 'pre-wrap', fontSize: 16 }}
      >
        {body}
      </article>

      <section
        data-testid={testIds.learnLora.faq}
        style={{
          marginTop: 48,
          paddingTop: 24,
          borderTop: '1px solid #ddd',
        }}
      >
        <h2 style={{ fontSize: 24, marginTop: 0 }}>FAQ</h2>
        {FAQ.map((entry) => (
          <details
            key={entry.q}
            style={{
              marginBottom: 12,
              padding: '8px 12px',
              background: '#f7f6f1',
              border: '1px solid #ddd',
              borderRadius: 4,
            }}
            data-testid={testIds.learnLora.faqEntry(entry.q)}
          >
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
              {entry.q}
            </summary>
            <p style={{ margin: '8px 0 0', color: '#222' }}>{entry.a}</p>
          </details>
        ))}
      </section>

      <section
        style={{
          marginTop: 48,
          padding: 24,
          background: '#fffbcc',
          border: '1px solid #b08800',
          borderRadius: 4,
        }}
      >
        <h3 style={{ marginTop: 0, fontSize: 20 }}>Try one in the Lab</h3>
        <p style={{ margin: '0 0 12px' }}>
          The Specialization Lab runs the same prompt twice — once on
          the base model, once with your specialization layered on — and
          shows the two outputs side by side. It's the fastest way to
          feel what the overlay does.
        </p>
        <button
          type="button"
          data-testid={testIds.learnLora.ctaButton}
          onClick={() => {
            void navigate({ to: '/' });
          }}
          style={{
            background: '#003c74',
            color: '#fff',
            border: 0,
            padding: '8px 18px',
            borderRadius: 3,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Open Repancy →
        </button>
      </section>
    </div>
  );
}
